import React from "react";

/**
 * Default illustration for the result hero card.
 * Shows a stylised abstract "constellation" motif in the Mindovia terracotta palette.
 * Falls back gracefully when no custom illustration is given.
 *
 * Props:
 *  - src?: string  — external image URL, when provided we render it as <img> instead
 *  - alt?: string  — accessibility label
 *  - size?: "sm" | "md" | "lg"
 */
export default function ResultIllustration({ src, alt = "", size = "md" }) {
  const dim = size === "sm" ? "w-24 h-24" : size === "lg" ? "w-56 h-56 md:w-72 md:h-72" : "w-40 h-40 md:w-48 md:h-48";
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`${dim} object-contain rounded-2xl select-none`}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        data-testid="result-illustration-img"
      />
    );
  }
  return (
    <svg
      viewBox="0 0 240 240"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={alt || "Mindovia illustration"}
      className={`${dim} select-none`}
      data-testid="result-illustration-svg"
    >
      <defs>
        <radialGradient id="riBg" cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#f7d9c8" />
          <stop offset="100%" stopColor="#e6b39a" />
        </radialGradient>
        <linearGradient id="riAccent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#c46b4f" />
          <stop offset="100%" stopColor="#8a3f2c" />
        </linearGradient>
      </defs>
      <circle cx="120" cy="120" r="112" fill="url(#riBg)" />
      <circle cx="120" cy="120" r="112" fill="none" stroke="#c46b4f" strokeOpacity="0.35" strokeWidth="2" />
      {/* Central 4-point star */}
      <path
        d="M120 46
           L133 108
           L182 120
           L133 132
           L120 194
           L107 132
           L58 120
           L107 108 Z"
        fill="url(#riAccent)"
      />
      {/* Small accent stars */}
      <path
        d="M182 72 L187 88 L202 92 L187 96 L182 112 L177 96 L162 92 L177 88 Z"
        fill="#fdf6ee" fillOpacity="0.95"
      />
      <path
        d="M60 158 L64 168 L74 172 L64 176 L60 186 L56 176 L46 172 L56 168 Z"
        fill="#fdf6ee" fillOpacity="0.9"
      />
      <circle cx="185" cy="165" r="6" fill="#fdf6ee" />
      <circle cx="55" cy="90" r="4" fill="#fdf6ee" opacity="0.85" />
    </svg>
  );
}
